import { jsx } from 'react/jsx-runtime';
import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { initializeDevCycle } from '@devcycle/js-client-sdk';
import hoistNonReactStatics from 'hoist-non-react-statics';

const context = createContext(undefined);
const { Provider, Consumer } = context;
const initializedContext = createContext({
    isInitialized: false,
});

const initializeDevCycleClient = (sdkKey, user = { isAnonymous: true }, options) => {
    if (options === null || options === void 0 ? void 0 : options.deferInitialization) {
        return initializeDevCycle(sdkKey, {
            ...options,
            deferInitialization: true,
            bootstrapConfig: undefined,
        });
    }
    return initializeDevCycle(sdkKey, user, options);
};

/**
 * @deprecated Use the `useIsDevCycleInitialized` hook to block rendering of your application
 *             until SDK initialization is complete
 */
async function asyncWithDVCProvider(config) {
    const { user, options } = config;
    let sdkKey;
    if ('sdkKey' in config) {
        sdkKey = config.sdkKey;
    }
    else {
        sdkKey = config.envKey;
    }
    if (!sdkKey) {
        throw new Error('You must provide a sdkKey to asyncWithDVCProvider');
    }
    const client = initializeDevCycleClient(sdkKey, user, options);
    await client.onClientInitialized();
    return ({ children }) => {
        return jsx(Provider, { value: { client }, children: children });
    };
}

const useDevCycleClient = () => {
    const dvcContext = useContext(context);
    if (dvcContext === undefined)
        throw new Error('useDevCycleClient must be used within DevCycleProvider');
    return dvcContext.client;
};
/**
 * @deprecated use useDevCycleClient instead
 */
const useDVCClient = useDevCycleClient;

const useVariable = (key, defaultValue) => {
    const dvcContext = useContext(context);
    const [_, forceRerender] = useState({});
    const forceRerenderCallback = useCallback(() => forceRerender({}), []);
    if (dvcContext === undefined)
        throw new Error('useVariable must be used within DevCycleProvider');
    useEffect(() => {
        dvcContext.client.subscribe(`variableUpdated:${key}`, forceRerenderCallback);
        return () => {
            dvcContext.client.unsubscribe(`variableUpdated:${key}`, forceRerenderCallback);
        };
    }, [dvcContext, key, forceRerenderCallback]);
    return dvcContext.client.variable(key, defaultValue);
};

const useVariableValue = (key, defaultValue) => {
    return useVariable(key, defaultValue).value;
};

function DevCycleProvider(props) {
    const { config } = props;
    const { user, options } = config;
    const [isInitialized, setIsInitialized] = useState(false);
    let sdkKey;
    if ('sdkKey' in config) {
        sdkKey = config.sdkKey;
    }
    else {
        sdkKey = config.envKey;
    }
    if (!sdkKey) {
        throw new Error('You must provide a sdkKey to DevCycleProvider');
    }
    const clientRef = useRef();
    const [_, forceRerender] = useState({});
    if (clientRef.current === undefined) {
        clientRef.current = initializeDevCycleClient(sdkKey, user, {
            ...options,
        });
    }
    useEffect(() => {
        if (clientRef.current === undefined) {
            clientRef.current = initializeDevCycleClient(sdkKey, user, {
                ...options,
            });
            // react doesn't know the effect changed the ref, make sure it re-renders
            forceRerender({});
        }
        clientRef.current
            .onClientInitialized()
            .then(() => {
            setIsInitialized(true);
        })
            .catch(() => {
            // set to true to unblock app load
            console.log('Error initializing DevCycle.');
            setIsInitialized(true);
        });
        return () => {
            var _a;
            void ((_a = clientRef.current) === null || _a === void 0 ? void 0 : _a.close());
            clientRef.current = undefined;
        };
    }, [sdkKey, user, options]);
    return (jsx(Provider, { value: { client: clientRef.current }, children: jsx(initializedContext.Provider, { value: {
                isInitialized: isInitialized || clientRef.current.isInitialized,
            }, children: props.children }) }));
}
/**
 * @deprecated Use DevCycleProvider instead
 */
const DVCProvider = DevCycleProvider;

function withDevCycleProvider(config) {
    return (WrappedComponent) => {
        const HoistedComponent = (props) => {
            return (jsx(DevCycleProvider, { config: config, children: jsx(WrappedComponent, { ...props }) }));
        };
        hoistNonReactStatics(HoistedComponent, WrappedComponent);
        return HoistedComponent;
    };
}
/**
 * @deprecated Use withDevCycleProvider instead
 */
const withDVCProvider = withDevCycleProvider;

/**
 *
 * @deprecated Use the `useVariable` hook instead
 *
 */
const useDVCVariable = (key, defaultValue) => {
    return useVariable(key, defaultValue);
};

const useIsDevCycleInitialized = () => {
    const context = useContext(initializedContext);
    if (context === undefined)
        throw new Error('useIsDevCycleInitialized must be used within DevCycleProvider');
    return context.isInitialized;
};
/**
 * @deprecated use useIsDevCycleInitialized instead
 */
const useIsDVCInitialized = useIsDevCycleInitialized;

export { DVCProvider, DevCycleProvider, asyncWithDVCProvider, useDVCClient, useDVCVariable, useDevCycleClient, useIsDVCInitialized, useIsDevCycleInitialized, useVariable, useVariableValue, withDVCProvider, withDevCycleProvider };
